import { throwError as observableThrowError, Observable } from "rxjs";

import { finalize, tap, catchError, retry } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { AuthService } from "./auth.service";
import { Router } from "@angular/router";
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpParameterCodec, HttpParams, HttpResponse } from "@angular/common/http";

/**
 * Encodes every key and value with the full encodeURIComponent. Angular's default
 * codec deliberately leaves characters like + ? / : @ un-encoded, which can corrupt
 * values server-side. This makes encoding total and unambiguous.
 */
class FullEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

interface RequestOptionsCustom {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: any;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType?: any;
  withCredentials?: boolean;
}

@Injectable()
export class HttpService extends HttpClient {
  constructor(
    backend: HttpBackend,
    // defaultOptions: RequestOptions,
    private router: Router,
    private authService: AuthService
  ) {
    super(backend);
  }

  /**
   * Normalizes the `params` option into a fully-encoded HttpParams instance.
   * Pass params as a plain object (or HttpParams) and encoding is guaranteed —
   * never concatenate query values into the URL string. null/undefined values
   * are skipped and arrays are expanded into repeated keys.
   */
  private buildParams(params?: any): HttpParams | undefined {
    if (!params) return undefined;
    if (params instanceof HttpParams) return params;
    let hp = new HttpParams({ encoder: new FullEncoder() });
    for (const [key, val] of Object.entries(params)) {
      if (val === null || val === undefined) continue;
      if (Array.isArray(val)) {
        val.forEach((v) => (hp = hp.append(key, String(v))));
      } else {
        hp = hp.append(key, String(val));
      }
    }
    return hp;
  }

  get(url: string, options: any = {}): Observable<any> {
    options = { ...options, params: this.buildParams(options.params) };
    return super.get(url, options).pipe(
      retry(3),
      catchError(this.onCatch),
      tap(
        (res: HttpResponse<any>) => {
          this.onSuccess(res);
        },
        (error: HttpErrorResponse) => {
          this.onError(error);
        }
      ),
      finalize(() => {
        this.onEnd();
      })
    );
  }

  // post(url: string, body: any, options?: { headers?: HttpHeaders | { [header: string]: string | string[]; }}): Observable<any> {
  post(
    url: string,
    body: any,
    options: any = {}
  ): Observable<any> {
    options = { ...options, params: this.buildParams(options.params) };
    return super.post(url, body, options).pipe(
      catchError(this.onCatch),
      tap(
        (res: HttpResponse<any>) => {
          this.onSuccess(res);
        },
        (error: any) => {
          this.onError(error);
        }
      ),
      finalize(() => {
        this.onEnd();
      })
    );
  }

  private onCatch(error: any, caught: Observable<any>): Observable<any> {
    return observableThrowError(error);
  }

  private onSuccess(res: HttpResponse<any>): void {}

  private onError(res: HttpErrorResponse): void {
    if (res.status === 401 || res.status === 403) {
      this.authService.logout();
      this.router.navigate(["/login"], {
        queryParams: { returnUrl: this.router.url },
      });
    }
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      console.log("An error cc");
    } else {
      this.authService.logout();
      this.router.navigate(["/login"], {
        queryParams: { returnUrl: this.router.url },
      });
    }
  }

  private onEnd(): void {}
}
